/**
 * The `creature` save slice (roster, active creature, DNA). P1 owns the shape and its migrations (architecture §4.4).
 * DNAs are stored as `M…` share strings (creatures §A12.2) so a save is human-readable and version-tagged.
 */
import type { SliceDef } from '../shared';
import type { AtelierAppearance } from '../shared/atelier';

export interface CreatureRow {
  /** Stable creature id (e.g. 'c1'). */
  id: string;
  /** `M…` share string (+ `.name` suffix for custom names). */
  dna: string;
  /** Wall ms of the last «Смена вида» (GDD C114), null = never. */
  speciesChangedAt: number | null;
  /** Full GLB atelier recipe, kept alongside gameplay DNA. Old saves remain valid. */
  appearance?: AtelierAppearance;
  portrait?: string;
}
export interface CreatureSave {
  v: 1;
  activeId: string | null;
  roster: CreatureRow[];
  /** Constructor drafts and FTUE progress live in the ui slice / localStorage (creatures §A7.3). */
  [k: string]: unknown;
}

const isRow = (r: unknown): r is CreatureRow =>
  typeof r === 'object' && r !== null && typeof (r as CreatureRow).id === 'string' && typeof (r as CreatureRow).dna === 'string';

export const creatureSlice: SliceDef<CreatureSave> = {
  key: 'creature',
  version: 1,
  create: () => ({ v: 1, activeId: null, roster: [] }),
  migrations: {},
  validate: (x) => {
    if (typeof x !== 'object' || x === null || typeof (x as { v?: unknown }).v !== 'number') return ['creature: expected an object with a numeric v'];
    const s = x as Partial<CreatureSave>;
    const errs: string[] = [];
    if (s.roster !== undefined && (!Array.isArray(s.roster) || !s.roster.every(isRow))) errs.push('creature.roster: expected {id, dna}[]');
    if (s.activeId !== undefined && s.activeId !== null && typeof s.activeId !== 'string') errs.push('creature.activeId: expected a string or null');
    return errs;
  },
};
